import Buttons from '@ui/elements/Buttons'
import Div from '@ui/layout/Div'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Modal from '@ui/component/Modal'
import CitizenSterilizationCallMaker from '@scene/window/sterilization/CitizenSterilizationCallMaker'
import CompanySterilizationCallMaker from '@scene/window/sterilization/CompanySterilizationCallMaker'
import useDownloadFile from '@hook/useDownloadFile'
import useDynamicForm from '@hook/useDynamicForm'
import DropDown from '@ui/component/DropDown'
import ProgressBar from '@ui/elements/ProgressBar'

const SterilizationListCommands = () => {
  const [t] = useTranslation('common')
  const [formType, setFormType] = useState(false)
  const [photosExport, setPhotosExport] = useState({ status: 'idle', percent: null, loaded: 0 })
  const toggleDynamicForm = useDynamicForm()
  const { selectedSeason } = useSelector(state => state.sterilization)
  const { mission } = useSelector(state => state.main)
  const { mairie, missionTitle, isCompany, activeSeason } = mission

  const downLoadFile = useDownloadFile()
  const missionFullTitle = t('labels.header_intro', { missionTitle, targetMairie: mairie.name, year: new Date().getFullYear() })

  const exportCalls = (format, scan = 'global') => {
    let query = `mission=${mission.id}&scan=${scan}&season=${selectedSeason}`
    const extension = "csv" === format ? "csv" : "pdf"
    const fileName = `
    ${missionFullTitle}-${'firstScan' === scan
        ? t('labels.first_scan')
        : ('secondScan' === scan ? t('labels.second_scan') : t('labels.full_scan'))
      }`
    downLoadFile(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/calls/export/${format}?${query}`, fileName, extension)
  }

  const isExportingPhotos = ['preparing', 'downloading'].includes(photosExport.status)

  const exportSeasonPhotos = () => {
    if (isExportingPhotos) {
      return
    }
    const extension = "zip"
    const fileName = `${missionFullTitle}-${selectedSeason}-photos`
    setPhotosExport({ status: 'preparing', percent: null, loaded: 0 })
    downLoadFile(
      `${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/seasons/${selectedSeason}/zip-photos`,
      fileName,
      extension,
      ({ loaded, percent }) => setPhotosExport(previous => {
        const isSameDisplay = 'downloading' === previous.status
          && previous.percent === percent
          && Math.floor(previous.loaded / 1048576) === Math.floor(loaded / 1048576)
        return isSameDisplay ? previous : { status: 'downloading', percent, loaded }
      })
    )
      .then(() => setPhotosExport({ status: 'idle', percent: null, loaded: 0 }))
      .catch(() => setPhotosExport({ status: 'error', percent: null, loaded: 0 }))
  }

  const renderPhotosExportStatus = () => {
    switch (photosExport.status) {
      case 'preparing':
        return (
          <>
            <p className='is-size-7'>{t('messages.zip_photos_preparing')}</p>
            <ProgressBar />
          </>
        )
      case 'downloading':
        return (
          <>
            <p className='is-size-7'>
              {t('messages.zip_photos_downloading', { size: Math.floor(photosExport.loaded / 1048576) })}
              {null !== photosExport.percent && ` (${photosExport.percent} %)`}
            </p>
            <ProgressBar progress={null !== photosExport.percent ? photosExport.percent : undefined} />
          </>
        )
      case 'error':
        return <p className='is-size-7 has-text-danger'>{t('messages.zip_photos_error')}</p>
      default:
        return null
    }
  }

  const getDynamicForm = () => {
    switch (formType) {
      case 'citizenCall':
        return <CitizenSterilizationCallMaker />
      case 'companyCall':
        return <CompanySterilizationCallMaker />
    }
  }

  const dropConfig = {
    title: t('actions.export_csv_calls'),
    menu: [
      {
        title: t('actions.export_csv_calls_first_scan'),
        text: t('actions.export_csv_calls_first_scan'),
        link: '#',
        otherProps: { onClick: () => exportCalls('csv', 'firstScan') }
      },
      {
        title: t('actions.export_csv_calls_second_scan'),
        text: t('actions.export_csv_calls_second_scan'),
        link: '#',
        otherProps: { onClick: () => exportCalls('csv', 'secondScan') }
      },
      {
        title: t('actions.export_csv_calls_global'),
        text: t('actions.export_csv_calls_global'),
        link: '#',
        otherProps: { onClick: () => exportCalls('csv', 'global') }
      },
    ]
  }

  return (
    <>
      <Div cssClass='actions-zone'>
        <DropDown dropConfig={dropConfig} cssClass='csv-exporter' mainButtonCssClass='is-small' />
        <Buttons buttons={[
          {
            icon: 'icon-clipboard',
            cssClass: 'is-link is-inverted is-small',
            callback: () => exportCalls('pdf'),
            text: t('actions.export_pdf_calls')
          },
          {
            icon: 'icon-download',
            cssClass: `is-success is-inverted is-small ${isExportingPhotos ? 'is-loading' : ''}`,
            disabled: isExportingPhotos,
            callback: () => exportSeasonPhotos(),
            text: t('actions.export_season_zip_photos', { season: activeSeason.name })
          },
          {
            icon: 'icon-add',
            cssClass: 'is-info is-small',
            callback: () => setFormType('citizenCall'),
            text: t('actions.add_citizen_call')
          }, {
            icon: 'icon-add',
            cssClass: `is-info is-small ${!isCompany ? 'is-hidden' : ''}`,
            callback: () => setFormType('companyCall'),
            text: t('actions.add_company_call')
          }, {
            icon: 'icon-upload',
            cssClass: 'is-warning is-small',
            callback: () => toggleDynamicForm('loadCalls'),
            text: t('actions.load_calls')
          }
        ]} />
      </Div>
      {'idle' !== photosExport.status && (
        <Div cssClass='photos-export-status'>
          {renderPhotosExportStatus()}
        </Div>
      )}
      <Modal cssClass='call-form-modal' isActive={formType} closeCallback={() => setFormType(false)} closeAction={true}>
        {getDynamicForm()}
      </Modal>
    </>
  )
}

export default SterilizationListCommands